import {PageSection} from '@gettactic/api';
import {findDivisionPathByOfficeId} from '@gettactic/helpers/src/divisions';
import React from 'react';
import {usePageSectionsQuery} from "../usePageSectionsQuery";
import {useDivisionsMenuQuery} from '../useDivisionsMenuQuery';

export interface UseGetPageSectionsForOfficeOptions {
    parentSectionId?: string;
    onlyRootSections?: boolean;
    allowEmptyOffice?: boolean;
    childrenAsRootIfSingleRoot?: boolean;
}

export type UseGetPageSectionsForOfficeReturn = (PageSection & {
    parent?: PageSection;
})[];

export const useGetPageSectionsForOffice = (
    selectedOfficeId: string,
    options?: UseGetPageSectionsForOfficeOptions
): UseGetPageSectionsForOfficeReturn => {
    const {data: divisions} = useDivisionsMenuQuery();
    const {data: sections} = usePageSectionsQuery();

    return React.useMemo(() => {
        if (!sections || (!selectedOfficeId && !options?.allowEmptyOffice)) {
            return [];
        }


        const pageSectionsRoot = sections || [];

        const divisionPath = findDivisionPathByOfficeId(
            divisions?.tree?.children || [],
            selectedOfficeId
        );

        const foundSections: PageSection[] = [];
        if (pageSectionsRoot.length > 0) {
            pageSectionsRoot.forEach((section) => {
                if (
                    (options?.allowEmptyOffice && !selectedOfficeId) ||
                    (section.division_id &&
                        divisionPath?.includes(section.division_id)) ||
                    (section.office_id && section.office_id === selectedOfficeId)
                ) {
                    foundSections.push(section);
                }
            });
        }

        if (options?.parentSectionId) {
            return foundSections.filter(
                (section) => section.parent_id === options.parentSectionId
            );
        }

        if (options?.onlyRootSections) {
            const rootSections = foundSections.filter(
                (section) => !section.parent_id
            );
            if (options.childrenAsRootIfSingleRoot && rootSections.length === 1) {
                return foundSections
                    .filter((section) => section.parent_id === foundSections[0].id)
                    .map((section) => {
                        return {
                            ...section,
                            parent: rootSections[0]
                        };
                    });
            }
            return rootSections;
        }

        return foundSections;
    }, [
        sections,
        selectedOfficeId,
        options?.parentSectionId,
        options?.allowEmptyOffice,
        options?.onlyRootSections,
        options?.childrenAsRootIfSingleRoot
    ]);
};
